/**
 * Azure Storage tree-node factories (spec §5.1). Ids derive from
 * `storageResourceKey` (ruling F1: typed identity, never `database = service`);
 * the connection branch emits one service folder per implemented, enabled,
 * reachable service (O1: no permanently empty folders), or the single rooted
 * branch of a resource-scoped attachment.
 */
import type { Connection } from '../../../types/electron';
import {
  storageResourceKey,
  storageServiceOf,
  type StorageAccountInfo,
  type StorageItem,
  type StorageResourceRef,
  type StorageService,
} from '../../../types/azureStorage';
import type { PinnedObjectRef } from '../../../types/electron';
import { createNodeId } from '../utils/nodeIdUtils';
import { dataAccessNoteOf } from './storageDataAccessNote';
import { shareLabel } from '../../workspace/storage-explorer/files/fileTabTitle';
import type {
  DatabaseExplorerNode,
  PinnedStorageNode,
  StorageNodeMetadata,
  StorageNodeType,
  StorageResourceNode,
  StorageServiceFolderNode,
} from './nodeTypes';

/** Services whose tree branch has landed. File Shares join in S7 (O1 rule). */
export const IMPLEMENTED_STORAGE_SERVICES: readonly StorageService[] = [
  'blob',
  'queue', // S5
  'table', // S4
  'file', // S7
];
const ALL_STORAGE_SERVICES: readonly StorageService[] = ['blob', 'table', 'queue', 'file'];

const STORAGE_NODE_TYPES: readonly string[] = ['storage-service-folder', 'storage-container', 'storage-table', 'storage-queue', 'storage-share', 'storage-action'];
const STORAGE_RESOURCE_NODE_TYPES: readonly string[] = ['storage-container', 'storage-table', 'storage-queue', 'storage-share'];

export function isStorageNodeType(type: string): type is StorageNodeType {
  return STORAGE_NODE_TYPES.includes(type);
}

export function isStorageResourceNodeType(type: string): type is StorageResourceNode['type'] {
  return STORAGE_RESOURCE_NODE_TYPES.includes(type);
}

export function storageNodeId(connectionId: string, ref: StorageResourceRef): string {
  return createNodeId('storage', connectionId, storageResourceKey(ref));
}

export function storageServiceFolderName(service: StorageService, hns = false): string {
  switch (service) {
    case 'blob':
      return hns ? 'Data Lake Containers' : 'Blob Containers';
    case 'table':
      return 'Tables';
    case 'queue':
      return 'Queues';
    case 'file':
      return 'File Shares';
  }
}

export function storageNodeTypeForService(service: StorageService): StorageResourceNode['type'] {
  switch (service) {
    case 'blob':
      return 'storage-container';
    case 'table':
      return 'storage-table';
    case 'queue':
      return 'storage-queue';
    case 'file':
      return 'storage-share';
  }
}

export function createStorageServiceFolderNode(args: {
  connectionId: string;
  parentId: string;
  service: StorageService;
  hns?: boolean;
  availability?: StorageNodeMetadata['availability'];
  dataAccessNote?: StorageNodeMetadata['dataAccessNote'];
}): StorageServiceFolderNode {
  const { connectionId, parentId, service, hns, availability, dataAccessNote } = args;
  const resource: StorageResourceRef = { kind: 'service', service };
  return {
    id: storageNodeId(connectionId, resource),
    parentId,
    name: storageServiceFolderName(service, hns),
    type: 'storage-service-folder',
    canHaveChildren: true,
    isExpanded: false,
    children: [],
    metadata: { connectionId, engine: 'azurestorage', service, resource, ...(availability ? { availability } : {}), ...(dataAccessNote ? { dataAccessNote } : {}) },
  };
}

/** A container / table / queue / share leaf from a listing item; `null` for items the tree never shows (blobs, folders, files). */
export function createStorageResourceNode(args: { connectionId: string; parentId: string; item: StorageItem }): StorageResourceNode | null {
  const { connectionId, parentId, item } = args;
  switch (item.kind) {
    case 'container':
      return storageResourceNode(connectionId, parentId, { kind: 'container', container: item.name }, item.name, {
        ...(item.publicAccess ? { publicAccess: item.publicAccess } : {}),
        ...(item.hns ? { hns: true } : {}),
        ...(item.leaseState && item.leaseState !== 'available' ? { leased: true } : {}),
        ...(item.deleted ? { deleted: true } : {}),
        ...(item.name.startsWith('$') ? { system: true } : {}),
      });
    case 'table':
      return storageResourceNode(connectionId, parentId, { kind: 'table', table: item.name }, item.name, {});
    case 'queue':
      return storageResourceNode(connectionId, parentId, { kind: 'queue', queue: item.name }, item.name, {
        ...(item.approximateMessageCount !== undefined ? { approximateMessageCount: item.approximateMessageCount } : {}),
      });
    case 'share':
      return storageResourceNode(connectionId, parentId, { kind: 'share', share: item.name, ...(item.snapshot ? { snapshot: item.snapshot } : {}) }, shareLabel(item.name, item.snapshot), {
        ...(item.quotaGiB !== undefined ? { quotaGiB: item.quotaGiB } : {}),
        ...(item.protocol ? { protocol: item.protocol } : {}),
        ...(item.source ? { shareSource: item.source } : {}),
      });
    default:
      return null;
  }
}

function storageResourceNode(
  connectionId: string,
  parentId: string,
  resource: StorageResourceRef,
  name: string,
  badges: Partial<StorageNodeMetadata>,
): StorageResourceNode {
  const service = storageServiceOf(resource) ?? 'blob';
  return {
    id: storageNodeId(connectionId, resource),
    parentId,
    name,
    type: storageNodeTypeForService(service),
    canHaveChildren: false,
    isExpanded: false,
    children: [],
    metadata: { connectionId, engine: 'azurestorage', service, resource, ...badges },
  };
}

/** Human label of a scoped attachment's root (container / blob / …) for the single rooted branch. */
function scopedResourceLabel(ref: StorageResourceRef): string {
  switch (ref.kind) {
    case 'container':
      return ref.container;
    case 'blobPrefix':
      return ref.prefix ? `${ref.container}/${ref.prefix}` : ref.container;
    case 'blob':
      return `${ref.container}/${ref.name}`;
    case 'table':
      return ref.table;
    case 'queue':
      return ref.queue;
    case 'message':
      return `${ref.queue}/${ref.messageId}`;
    case 'share':
      return shareLabel(ref.share, ref.snapshot);
    case 'directory':
    case 'file':
      return ref.path ? `${shareLabel(ref.share, ref.snapshot)}/${ref.path}` : shareLabel(ref.share, ref.snapshot);
    case 'hnsPath': // S8: a directory-SAS attachment's root
      return ref.path ? `${ref.fileSystem}/${ref.path}` : ref.fileSystem;
    default:
      return storageResourceKey(ref);
  }
}

function singleItemRootLabel(ref: StorageResourceRef): { name: string; fullPath: string } | undefined {
  if (ref.kind !== 'file' && ref.kind !== 'blob') return undefined;
  const path = ref.kind === 'file' ? ref.path : ref.name;
  const name = path.split('/').filter(Boolean).pop();
  return name ? { name, fullPath: scopedResourceLabel(ref) } : undefined;
}

export type StorageConnectionShape = Pick<Connection, 'azureStorageScope' | 'azureStorageServices' | 'azureStorageEmulator'>;

/**
 * The storage connection's children (after the pinned run). Omits a service
 * when (a) the connection's `azureStorageServices` excludes it, (b) the
 * attachment is resource-scoped (only that resource's branch), (c) its slice
 * has not landed, (d) account-info reports it `unavailable` / unverified
 * for `sdk-not-installed` or `service-not-offered` (the account type has no such service), or (e) it is File Shares on an emulator; an
 * `unverified('sas-no-permitted-rung')` folder IS shown — its first listing is
 * the test (spec §4.5 outcome table) — and so is `unverified('share-names-required')`
 * (S7 §3.2: the folder stands with only its "Open share by name…" row).
 */
export function buildStorageConnectionChildren(args: {
  connectionId: string;
  parentId: string;
  connection: StorageConnectionShape | undefined;
  accountInfo?: StorageAccountInfo;
}): DatabaseExplorerNode[] {
  const { connectionId, parentId, connection, accountInfo } = args;
  const scope = connection?.azureStorageScope ?? { kind: 'account' as const };
  if (scope.kind === 'resource') {
    const service = storageServiceOf(scope.resource);
    if (!service || !IMPLEMENTED_STORAGE_SERVICES.includes(service)) return [];
    // S8: a blob-family root of an account whose hierarchical namespace is confirmed BY EVIDENCE carries the HNS badge (never inferred from `sr=d`).
    const hnsBadge = accountInfo?.hns === 'yes' && service === 'blob' && scope.resource.kind !== 'blob' ? { hns: true } : {};
    const root = storageResourceNode(connectionId, parentId, scope.resource, scopedResourceLabel(scope.resource), hnsBadge);
    // UI review F14: ONE file (a file SAS) or ONE blob reads as that file — its name, a file icon, the full path as the tooltip.
    const singleItem = singleItemRootLabel(scope.resource);
    return [singleItem ? { ...root, name: singleItem.name, icon: 'storage-file', metadata: { ...root.metadata, fullPath: singleItem.fullPath } } : root];
  }
  const enabled = connection?.azureStorageServices ?? ALL_STORAGE_SERVICES;
  const folders: DatabaseExplorerNode[] = [];
  for (const service of IMPLEMENTED_STORAGE_SERVICES) {
    if (!enabled.includes(service)) continue;
    const availability = accountInfo?.services[service];
    if (availability?.state === 'unavailable') continue;
    if (availability?.state === 'unverified' && (availability.reason === 'sdk-not-installed' || availability.reason === 'service-not-offered')) continue;
    if (service === 'file' && connection?.azureStorageEmulator) continue; // S7: Azurite has no File service
    const folder = createStorageServiceFolderNode({
      connectionId,
      parentId,
      service,
      hns: accountInfo?.hns === 'yes',
      ...(availability ? { availability: availability.state } : {}),
      dataAccessNote: availability?.state === 'ok' ? dataAccessNoteOf(availability.dataAccess) : undefined,
    });
    // S7 (§3.2): Entra without a share listing and without named shares — the folder stands with its "Open share by name…" row.
    if (availability?.state === 'unverified' && availability.reason === 'share-names-required') folder.metadata.availabilityDetail = availability.detail;
    folders.push(folder);
  }
  return folders;
}

export function createPinnedStorageNode(args: {
  connectionId: string;
  parentId: string;
  ref: Extract<PinnedObjectRef, { kind: 'storage' }>;
}): PinnedStorageNode {
  const { connectionId, parentId, ref } = args;
  const service = storageServiceOf(ref.resource) ?? 'blob';
  return {
    id: createNodeId('pinned-storage', connectionId, storageResourceKey(ref.resource)),
    parentId,
    name: ref.label,
    type: 'pinned-storage',
    canHaveChildren: false,
    isExpanded: false,
    children: [],
    metadata: { connectionId, engine: 'azurestorage', service, resource: ref.resource },
    pinRef: ref,
    resolution: 'unresolved',
  };
}
